/**
 * Copyright (c) 2017 CRF Box Oy. All Rights Reserved. Company Confidential.
 */
import React from 'react';
import PropTypes from 'prop-types';
import Loading from '../loading/loading.component';
import NotePanel from './note.panel';
import { NO_DATA_AVAILABLE } from '../../const.js';
import { Row, Grid, Col, FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { Button } from 'tc-client-common';
import NotesService from './notes.service';
import * as consts from '../../const.js';
import { generateIteratorKey as keygen } from '../../utils/helpers';
import DetailedTitledPage from '../../router/pages/detailed.titled.page';
import DocumentsService from '../documents/documents.service';

class NotesComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      documentNameFilter: null,
      timezone: this.props.timezone,
      loading: false,
      notesData: null,
      error: null
    };
  }

  createNoteDocumentsFilterSelection(notesData) {
    let documentNames = [];
    for (let note of notesData.notes.data) {
      let documentFullName = DocumentsService.buildDocumentFullName(note.documentName, note.documentVersion);
      if (!documentNames.includes(documentFullName)) {
        documentNames.push(documentFullName);
      }
    }

    let options = [];
    options.push(<option value={''}>{consts.SELECT_VALUE_ALL}</option>);
    for (let documentName of documentNames) {
      options.push(<option value={documentName}>{documentName}</option>);
    }
    return options;
  }

  componentDidMount() {
    this._mounted = true;
    this.setState({ loading: true });
    this.props
      .loadData(this.props.studyGid, this.props.participantId)
      .then((response) => {
        if (this._mounted) {
          this.setState({
            loading: false,
            notesData: response.data
          });
        }
      })
      .catch((error) => {
        this.setState({ loading: false, error: error });
      });
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.newNoteAdded !== this.props.newNoteAdded && this.props.newNoteAdded) {
      this.props
        .loadData(this.props.studyGid, this.props.participantId)
        .then((response) => {
          this.setState({
            notesData: response.data,
            error: null
          });
        })
        .catch((error) => {
          this.setState({ error: error });
        });
    }
  }

  onDocumentFilterChanged(event) {
    this.setState({
      documentNameFilter: event.target.value
    });
  }

  onDownloadButtonClick(event) {
    const { participantId } = this.props;
    const { studyGid } = this.props;
    this.props.fetchNotesPdf(studyGid, participantId);
  }

  buttonBar(downloadDisabled) {
    return (
      <div className="pull-right">
        <Button
          id="assigned-docs-add-note"
          handleClick={() => this.props.createNewNote(this.props.reporter)}
          label={consts.ADD_NOTE}
          theme="default large-button"
        />
        <Button
          id="download-notes"
          disabled={downloadDisabled}
          theme="default large-button"
          handleClick={() => this.onDownloadButtonClick()}
          label={consts.DOWNLOAD}
        />
      </div>
    );
  }

  render() {
    let body;
    if (this.state.loading) {
      body = <Loading />;
    } else if (this.state.error) {
      body = (
        <div id="notes-error-wrapper">
          {NO_DATA_AVAILABLE}
          {this.buttonBar(true)}
        </div>
      );
    } else {
      if (!this.state.notesData || !this.state.notesData.notes) {
        return (
          <div id="notes-no-data-wrapper">
            {NO_DATA_AVAILABLE}
            {this.buttonBar(true)}
          </div>
        );
      }
      let documentsFilter = (
        <FormControl
          onChange={this.onDocumentFilterChanged.bind(this)}
          componentClass="select"
          id="notes-document-selector"
        >
          {this.createNoteDocumentsFilterSelection(this.state.notesData)}
        </FormControl>
      );
      let rows = NotesService.processRows(this.state, this.state.documentNameFilter);
      let properties = new Map(NotesService.columnNames.map((i) => [i.key, i.name]));
      body = (
        <div>
          <Grid id="notes-grid" className="tc-grid" fluid>
            <Row>
              <Col xs={2} id="notes-documents-label">
                {consts.DOCUMENT}
              </Col>
              <Col xs={3}>{documentsFilter}</Col>
              <Col xs={7}>{this.buttonBar(false)}</Col>
            </Row>
            <Row id="notes-grid-header" className="tc-grid-header">
              <Col md={2} xs={2}>
                {properties.get('timestamp')}
              </Col>
              <Col md={2} xs={2}>
                {properties.get('reporter')}
              </Col>
              <Col md={8} xs={8}>
                {properties.get('note')}
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={12}>
                <div id="notes-wrapper" className="notes-wrapper">
                  <div className="scrollableNotes">
                    {rows.map((item, index) => (
                      <Row {...keygen()} className="notes-row">
                        <NotePanel id={'notePanel' + index} note={item} propertyKeys={NotesService.columnNames} />
                      </Row>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Grid>
        </div>
      );
    }
    let title = consts.NOTES_PANEL_TITLE;
    let details = <div>{consts.NOTES_PANEL_DETAILS_TEXT}</div>;
    let content = <div id="view-notes">{body}</div>;
    return <DetailedTitledPage title={title} details={details} content={content} />;
  }
}

NotesComponent.propTypes = {
  participantId: PropTypes.number.isRequired,
  studyGid: PropTypes.string.isRequired,
  loadData: PropTypes.func.isRequired,
  fetchNotesPdf: PropTypes.func.isRequired,
  newNoteAdded: PropTypes.bool.isRequired
};

export default NotesComponent;
